import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import PdfViewer from "../../../../../pages/reusable_components/PdfViewer/PdfViewer";
import { fetchDocumentForClient } from "../../../../../pages/client_onboarding/ClientFilesUploader/ClientFilesUploader.service"; 
import styles from "./ApprovalPdfViewerModal.module.css";

interface ApprovalPdfViewerModalProps {
  show: boolean;
  onHide: () => void;
  clientMasterId: number;
  clientName: string;
  pdfUrl?: string; // Optional prop to directly receive PDF URL (from grid data)
}

interface ApprovalDocument {
  ClientDocumentId: number;
  FirmId: number;
  ClientMasterId: number;
  ClientName: string;
  FileName: string;
  BlobUrl: string;
  DocumentTypeCode: string;
  DocumentTypeName: string;
  Status: string;
  Active: string;
  CreatedBy: string;
  CreatedDate: string;
  UpdatedBy: string;
  UpdatedDate: string;
}

const ApprovalPdfViewerModal: React.FC<ApprovalPdfViewerModalProps> = ({
  show,
  onHide,
  clientMasterId,
  clientName,
  pdfUrl: pdfUrlProp, 
}) => {
  // Internal state for fetched URL (used when pdfUrlProp is not provided)
  const [fetchedPdfUrl, setFetchedPdfUrl] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Determine the actual URL to use (prop takes priority over fetched)
  const effectivePdfUrl = pdfUrlProp || fetchedPdfUrl;

  useEffect(() => {
    if (show && clientMasterId) {
      // If pdfUrl prop is provided, use it directly (no API call needed)
      if (pdfUrlProp && pdfUrlProp.trim() !== "") {
        setPdfFileName(`Approval_${clientName}.pdf`);
        setError(null);
      } else {
        // Fall back to fetching via API if prop not provided
        fetchApprovalPdf();
      }
    }

    // Reset state when modal closes
    if (!show) {
      setFetchedPdfUrl(null);
      setPdfFileName("");
      setError(null);
    }
  }, [show, clientMasterId, pdfUrlProp]);

  const fetchApprovalPdf = async () => {
    setLoading(true);
    setError(null);
    setFetchedPdfUrl(null);

    try {
      const response: ApprovalDocument[] | undefined = await fetchDocumentForClient(
        clientMasterId,
        setLoading,
        "APPROVAL_PDF"
      );

      if (response && response.length > 0) {
        const document = response[0];

        if (document.BlobUrl) {
          setFetchedPdfUrl(document.BlobUrl);
          setPdfFileName(document.FileName || `Approval_${clientName}.pdf`);
        } else {
          setError("No PDF URL found in response.");
        }
      } else {
        setError("No approval PDF found for this client.");
      }
    } catch (err) {
      console.error("Error fetching approval PDF:", err);
      setError("Failed to load the approval PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFetchedPdfUrl(null);
    setPdfFileName("");
    setError(null);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      className={styles.approvalPdfModal}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        {/* <Modal.Title>Approval Document - {clientName}</Modal.Title> */}
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {loading && (
          <div className={styles.loadingContainer}>
            <Spinner animation="border" role="status" />
            <span className={styles.loadingText}>Loading PDF...</span>
          </div>
        )}

        {error && !loading && (
          <div className={styles.errorContainer}>
            <span className={styles.errorText}>{error}</span>
          </div>
        )}

        {effectivePdfUrl && !loading && !error && (
          <PdfViewer
            preview={{
              url: effectivePdfUrl,
              name: pdfFileName,
            }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ApprovalPdfViewerModal;